import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OnboardingV2State } from '../../../../store/onboardingV2/types.ts';
import { useHorizontalDragScroll } from '../../../../hooks/onboardingV2/useHorizontalDragScroll.ts';
import SearchInput from '../SearchInput/SearchInput.tsx';
import styles from './SkillsSelector.module.scss';
import { ArrowLeft, Check } from 'lucide-react';

interface SkillsSelectorProps {
  state: OnboardingV2State;
  onAction: (action: string, data?: any) => void;
  setSelectedCategoryFilter?: (categoryId: number | null) => void;
}

const SkillsSelector: React.FC<SkillsSelectorProps> = ({
  state,
  onAction,
  setSelectedCategoryFilter,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [lastAddedSkillId, setLastAddedSkillId] = useState<number | null>(null);

  const skillCategories = useMemo(() => {
    return state.skillCategories || [];
  }, [state.skillCategories]);

  const filteredSkills = useMemo(() => {
    let skills = state.skills;

    if (searchTerm.trim()) {
      skills = skills.filter((skill) =>
        skill.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (state.selectedCategoryFilter !== null) {
      skills = skills.filter(
        (skill) => skill.categoryId === state.selectedCategoryFilter
      );
    }

    return skills.slice(0, 50);
  }, [searchTerm, state.skills, state.selectedCategoryFilter]);

  const categoryScroll = useHorizontalDragScroll([
    searchTerm,
    state.selectedCategoryFilter,
    skillCategories.length,
  ]);
  const skillsScroll = useHorizontalDragScroll([
    searchTerm,
    state.selectedCategoryFilter,
    filteredSkills.length,
  ]);

  const handleCategoryClick = (categoryId: number) => {
    if (!setSelectedCategoryFilter) return;
    if (state.selectedCategoryFilter === categoryId) {
      setSelectedCategoryFilter(null);
    } else {
      setSelectedCategoryFilter(categoryId);
      setSearchTerm('');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (setSelectedCategoryFilter && value.trim()) {
      setSelectedCategoryFilter(null);
    }
  };

  return (
    <motion.div
      className={styles.interactiveContainer}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className={styles.skillsSection}>
        <SearchInput
          value={searchTerm}
          placeholder="Escribí el nombre de una habilidad para buscarla..."
          onChange={handleSearchChange}
          onClear={() => handleSearchChange('')}
          showClear={!!searchTerm}
          showAction={state.userSkills.length > 0}
          actionLabel="Continuar"
          onAction={() => onAction('skillsComplete')}
          containerClassName={styles.skillsSearch}
          inputClassName={styles.skillsInput}
          iconClassName={styles.searchIcon}
          clearButtonClassName={styles.clearButton}
          actionClassName={`${styles.skillsContinueButton} ${styles.skillsContinueButtonInline}`}
        />

        {!searchTerm && state.selectedCategoryFilter === null && (
          <div
            className={styles.categoryFilters}
            ref={categoryScroll.ref}
            onMouseDown={categoryScroll.onMouseDown}
            onMouseMove={categoryScroll.onMouseMove}
            onMouseUp={categoryScroll.onMouseUp}
            onMouseLeave={categoryScroll.onMouseLeave}
          >
            {skillCategories.map((category) => (
              <motion.button
                key={category.id}
                className={`${styles.categoryPill} ${state.selectedCategoryFilter === category.id ? styles.active : ''}`}
                onClick={() => handleCategoryClick(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
              >
                {category.name}
              </motion.button>
            ))}
          </div>
        )}

        {!searchTerm && state.selectedCategoryFilter !== null && (
          <div className={styles.selectedCategoryHeader}>
            <button
              className={styles.backToCategories}
              onClick={() => setSelectedCategoryFilter?.(null)}
              type="button"
            >
              <ArrowLeft size={16} />
            </button>
            <span className={styles.selectedCategoryName}>
              {skillCategories.find(c => c.id === state.selectedCategoryFilter)?.name}
            </span>
          </div>
        )}

        {(searchTerm || state.selectedCategoryFilter !== null) && (
          <div
            className={styles.skillsGrid}
            ref={skillsScroll.ref}
            onMouseDown={skillsScroll.onMouseDown}
            onMouseMove={skillsScroll.onMouseMove}
            onMouseUp={skillsScroll.onMouseUp}
            onMouseLeave={skillsScroll.onMouseLeave}
          >
            {filteredSkills.length > 0 ? (
              <AnimatePresence initial={false}>
                {filteredSkills.map((skill) => {
                  const category = skillCategories.find((c) => c.id === skill.categoryId);
                  const isSelected = state.userSkills.some((s) => s.id === skill.id);
                  return (
                    <motion.button
                      key={skill.id}
                      className={`${styles.skillButton} ${
                        isSelected ? styles.skillButtonActive : ''
                      } ${lastAddedSkillId === skill.id ? styles.addedPulse : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          onAction('skill', { action: 'remove', skillId: skill.id });
                          return;
                        }
                        onAction('skill', { action: 'add', skill });
                        setSearchTerm('');
                        setLastAddedSkillId(skill.id);
                        setTimeout(() => {
                          setLastAddedSkillId((prev) => (prev === skill.id ? null : prev));
                        }, 350);
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.32, ease: 'easeOut' }}
                      type="button"
                    >
                      {isSelected && <Check size={16} className={styles.selectedCheck} />}
                      {skill.name}
                      {category && searchTerm && (
                        <span className={styles.categoryTag}>{category.name}</span>
                      )}
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            ) : (
              <p className={styles.emptyMessage}>
                {searchTerm ? 'No se encontraron habilidades' : 'No hay habilidades disponibles en esta categoría'}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SkillsSelector;
